/**
 * Rent Residence Form Component
 * Reusable form for rent-specific fields
 */

import { DatePicker } from '@components/ui/DatePicker'
import { FormInput, FormField } from '@components/ui/FormField'
import { formatDateForAPI, formatDateForDisplay } from '@/features/applicant/utils/addressHelpers'
import { convertToDateInputFormat } from '@/lib/utils/dateValidation'
import { filterPhoneInput, formatCurrencyInput, getNumericCurrencyValue } from '@/lib/utils/formatters'
import { APPLICANT_CONTENT } from '@/features/applicant/constants/applicantContent'
import type { RentFormErrors } from './types'

interface RentFormProps {
  // Form values
  landlordName: string
  landlordEmail: string
  landlordMobile: string
  moveInDate: string
  leaseEndDate: string
  rentAmount: string
  monthsPaid?: string
  stillHere: boolean

  // Form handlers
  onLandlordNameChange: (value: string) => void
  onLandlordEmailChange: (value: string) => void
  onLandlordMobileChange: (value: string) => void
  onMoveInDateChange: (value: string) => void
  onLeaseEndDateChange: (value: string) => void
  onRentAmountChange: (value: string) => void
  onMonthsPaidChange?: (value: string) => void
  onStillHereChange: (value: boolean) => void

  // Validation errors
  errors?: RentFormErrors
  isReadOnly?: boolean
  isPreviousAddress?: boolean
}

export function RentForm({
  landlordName,
  landlordEmail,
  landlordMobile,
  moveInDate,
  leaseEndDate,
  rentAmount,
  monthsPaid,
  stillHere,
  onLandlordNameChange,
  onLandlordEmailChange,
  onLandlordMobileChange,
  onMoveInDateChange,
  onLeaseEndDateChange,
  onRentAmountChange,
  onMonthsPaidChange,
  onStillHereChange,
  errors = {},
  isReadOnly = false,
  isPreviousAddress = false,
}: RentFormProps) {
  return (
    <div className="border-l-4 border-blue-500 bg-gradient-to-r from-blue-50 to-indigo-50 p-6 mb-6 rounded-r-lg shadow-sm">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 [&>div]:min-w-0">
        <FormField
          label={APPLICANT_CONTENT.application.sections.currentAddress.rentFields.landlordName}
          required
        >
          <FormInput
            type="text"
            value={landlordName}
            onChange={(e) => onLandlordNameChange(e.target.value)}
            placeholder={APPLICANT_CONTENT.application.sections.currentAddress.rentFields.landlordName}
            error={errors.landlordName}
            disabled={isReadOnly}
          />
        </FormField>
        <FormField
          label={APPLICANT_CONTENT.application.sections.currentAddress.rentFields.landlordEmail}
          required
        >
          <FormInput
            type="email"
            value={landlordEmail}
            onChange={(e) => onLandlordEmailChange(e.target.value)}
            placeholder="landlord@example.com"
            error={errors.landlordEmail}
            disabled={isReadOnly}
          />
        </FormField>
        <FormField
          label={APPLICANT_CONTENT.application.sections.currentAddress.rentFields.landlordMobile}
          required
        >
          <FormInput
            type="text"
            value={landlordMobile}
            onChange={(e) => onLandlordMobileChange(filterPhoneInput(e.target.value))}
            placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.mobilePhone}
            error={errors.landlordMobile}
            disabled={isReadOnly}
          />
        </FormField>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 [&>div]:min-w-0">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {APPLICANT_CONTENT.application.sections.currentAddress.rentFields.moveInDate}*
          </label>
          <DatePicker
            value={moveInDate ? convertToDateInputFormat(moveInDate) : ''}
            onChange={(value) => {
              const displayValue = formatDateForDisplay(value)
              onMoveInDateChange(displayValue)
            }}
            placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.dateOfBirth}
            minDate={new Date(1900, 0, 1)}
            maxDate={
              leaseEndDate
                ? new Date(Math.min(new Date().getTime(), new Date(formatDateForAPI(leaseEndDate)).getTime()))
                : new Date()
            }
            showHelperText={false}
            error={errors.moveInDate}
            disabled={isReadOnly}
          />
        </div>

        {!isPreviousAddress && (
          <div>
            <div className="flex items-center gap-4 mb-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={stillHere}
                  onChange={(e) => {
                    if (!isReadOnly) {
                      onStillHereChange(e.target.checked)
                      if (e.target.checked) {
                        onLeaseEndDateChange('')
                      }
                    }
                  }}
                  disabled={isReadOnly}
                  className="mr-2 w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                />
                <span className="text-sm text-gray-700">{APPLICANT_CONTENT.application.sections.currentAddress.rentFields.stillHere}</span>
              </label>
              <span className="text-sm text-gray-700">{APPLICANT_CONTENT.application.sections.currentAddress.rentFields.leaseEndDate}</span>
            </div>
            <DatePicker
              value={leaseEndDate ? convertToDateInputFormat(leaseEndDate) : ''}
              onChange={(value) => {
                const displayValue = formatDateForDisplay(value)
                onLeaseEndDateChange(displayValue)
              }}
              placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.dateOfBirth}
              minDate={(() => {
                const today = new Date()
                today.setHours(0, 0, 0, 0)
                if (moveInDate) {
                  const moveIn = new Date(formatDateForAPI(moveInDate))
                  return moveIn > today ? moveIn : today
                }
                return today
              })()}
              allowFutureDates={true}
              disabled={isReadOnly || stillHere}
              showHelperText={false}
              error={errors.leaseEndDate}
            />
          </div>
        )}

        <FormField
          label={APPLICANT_CONTENT.application.sections.currentAddress.rentFields.rentAmount}
          required
        >
          <FormInput
            type="text"
            value={rentAmount ? formatCurrencyInput(rentAmount) : ''}
            onChange={(e) => {
              // Extract numeric value from formatted input
              const numericValue = getNumericCurrencyValue(e.target.value)
              // Store only the numeric value
              onRentAmountChange(numericValue)
            }}
            onBlur={() => {
              // On blur, strip any stray formatting so state stays numeric
              if (rentAmount) {
                const numeric = getNumericCurrencyValue(rentAmount)
                if (numeric !== rentAmount) {
                  onRentAmountChange(numeric)
                }
              }
            }}
            placeholder="$0.00"
            error={errors.rentAmount}
            disabled={isReadOnly}
          />
        </FormField>

        {onMonthsPaidChange && (
          <FormField
            label={APPLICANT_CONTENT.application.sections.currentAddress.rentFields.monthsPaidRent}
            required
          >
            <FormInput
              type="text"
              value={monthsPaid || '0'}
              onChange={() => { }}
              placeholder="0"
              error={errors.monthsPaidRent}
              disabled={true}
            />
          </FormField>
        )}
      </div>
    </div>
  )
}

